import React, { useEffect, useState } from 'react'
import { Stack } from 'react-bootstrap'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../common/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetAllEventFiltersQuery } from '../../../../store/eventFilter/eventFilter'
import { PostConnectStepFrame } from '../PostConnectStepFrame'
import { OnboardingStepHeader } from '../../Onboarding/OnboardingStepHeader/OnboardingStepHeader'
import { RadioCard } from '../Components/RadioCard'
import classNames from 'classnames'
import { CalendarEnterpriseFiltersView } from './CalendarEnterpriseFiltersView'
import styles from '../CrmScreens/CrmPostConnectSettings.module.scss'
import { POST_CONNECT_COPIES } from '../../copies/onboardingCopies'

type FilterChoice = 'default' | 'custom'

export interface CalendarEventFiltersStepProps {
  serviceId: IntegrationServiceId
  useConnectedHeader: boolean
  onContinue: () => void
  onExitToEventFilters: () => void
  onBack?: () => void
  isDone: boolean
}

export const CalendarEventFiltersStep: React.FC<CalendarEventFiltersStepProps> = ({
  serviceId,
  useConnectedHeader,
  onContinue,
  onExitToEventFilters,
  onBack,
  isDone,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: eventFilters, isLoading: isEventFiltersLoading } = useGetAllEventFiltersQuery({ accountId })
  const [choice, setChoice] = useState<FilterChoice>('default')

  const autoJoinFilter = eventFilters?.data.find((f) => f.type === 'auto_join')
  const importOnlyFilter = eventFilters?.data.find((f) => f.type === 'import_only')

  const isDefaultAutoJoinCondition = autoJoinFilter?.name === 'Default Event Filter' && autoJoinFilter?.createdAt === autoJoinFilter?.updatedAt

  const isEnterpriseMode = (autoJoinFilter?.conditions.some((c) => c.enforced) || importOnlyFilter?.conditions.some((c) => c.enforced)) ?? false

  const isCustom = choice === 'custom'

  useEffect(() => {
    if (!isEventFiltersLoading && !isDefaultAutoJoinCondition && !isEnterpriseMode) {
      onContinue()
    }
  }, [isEventFiltersLoading, isDefaultAutoJoinCondition, isEnterpriseMode, onContinue])

  return (
    <PostConnectStepFrame
      onAdvance={isCustom ? onExitToEventFilters : onContinue}
      onBack={onBack}
      nextLabel={isCustom ? 'Set up custom event filters' : isDone ? 'Done' : 'Next'}
      loading={isEventFiltersLoading}
    >
      <OnboardingStepHeader
        icon={useConnectedHeader ? <i className='bi bi-check-circle text-success' /> : <i className='bi bi-sliders2' />}
        title={useConnectedHeader ? `${getIntegrationDisplayName(serviceId)} connected` : POST_CONNECT_COPIES['calendar-event-filters'].headerTitle}
        subtitle={POST_CONNECT_COPIES['calendar-event-filters'].headerSubtitle}
      />

      {isEnterpriseMode ? (
        <CalendarEnterpriseFiltersView
          accountId={accountId}
          serviceId={serviceId}
          autoJoinFilter={autoJoinFilter}
          importOnlyFilter={importOnlyFilter}
          isLoading={isEventFiltersLoading}
        />
      ) : (
        <>
          <p className='m-0 fw-bold fs-5 text-center text-dark'>Set up auto-join rules</p>

          <div className={classNames(styles.settingCard, 'border')}>
            <div className='fw-semibold mb-2'>Join scheduled meetings</div>
            <Stack gap={2}>
              <RadioCard
                id='autofilter-default'
                name='autoFilter'
                value='default'
                checked={choice === 'default'}
                label='Use Zocks default settings'
                description={
                  <>
                    Join only to meetings <strong>under 10 hours</strong> and <strong>up to 5 participants</strong>
                  </>
                }
                onChange={() => setChoice('default')}
              />
              <RadioCard
                id='autofilter-custom'
                name='autoFilter'
                value='custom'
                checked={choice === 'custom'}
                label='Use custom settings'
                description='Customize your meeting list by setting Event Filters in Integrations'
                onChange={() => setChoice('custom')}
              />
            </Stack>
          </div>

          <div className='justify-content-center d-flex gap-2 text-secondary px-3'>
            <i className='bi bi-info-circle flex-shrink-0' />
            <small>Zocks only syncs meetings under 10 hours by default.</small>
          </div>
        </>
      )}
    </PostConnectStepFrame>
  )
}
